<script lang="ts">
  import { AgendaCentral, Consultorios, type AgendaCentralDestino, type Profesional } from '../lib/api';
  import HelpHint from './HelpHint.svelte';

  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let cargando = $state(false);
  let destinos = $state<AgendaCentralDestino[]>([]);
  let profesionales = $state<Profesional[]>([]);
  let consultorioId = $state('');
  let dia = $state(new Date().toISOString().slice(0, 10));
  let slots = $state<Array<{ inicio: string; fin?: string; ocupado?: boolean; consultorio?: string }>>([]);

  let pacienteNombre = $state('');
  let telefono = $state('');
  let profesional = $state('');
  let motivo = $state('');
  let hora = $state('09:00');
  let duracionMin = $state(30);

  const destinoSel = $derived(destinos.find(d => d.consultorioId === consultorioId));

  function fail(e: unknown) { error = e instanceof Error ? e.message : String(e); info = null; }
  function ok(m: string) { info = m; error = null; }

  async function cargarDestinos() {
    cargando = true;
    try {
      destinos = await AgendaCentral.destinos();
      profesionales = await Consultorios.profesionales().catch(() => []);
      if (!consultorioId && destinos.length) consultorioId = destinos[0].consultorioId;
    } catch (e) { fail(e); }
    finally { cargando = false; }
  }

  async function cargarSlots() {
    if (!consultorioId) return;
    cargando = true; error = null;
    try {
      const desde = `${dia}T00:00:00-06:00`;
      const hasta = `${dia}T23:59:59-06:00`;
      const r = await AgendaCentral.slots(consultorioId, desde, hasta);
      slots = (r.slots ?? []) as typeof slots;
    } catch (e) { fail(e); slots = []; }
    finally { cargando = false; }
  }

  async function agendar(e: SubmitEvent) {
    e.preventDefault();
    if (!consultorioId) return;
    try {
      const inicio = new Date(`${dia}T${hora}:00-06:00`).toISOString();
      await AgendaCentral.crear(consultorioId, {
        pacienteNombre,
        telefono,
        profesional,
        motivo,
        inicio,
        duracionMin,
      });
      ok('Cita registrada en la clínica/consultorio seleccionado.');
      await cargarSlots();
    } catch (ex) { fail(ex); }
  }

  $effect(() => { void cargarDestinos(); });
  $effect(() => { consultorioId; dia; void cargarSlots(); });
</script>

<section class="view agenda-central">
  <header class="view-header">
    <h1>Agenda central</h1>
    <p class="muted">Recepción hospital — citas en consultorios internos y clínicas atadas.</p>
    <HelpHint title="Guía rápida · Agenda">
      <p>Seleccione consultorio, día y profesional de la lista (médicos ya registrados). No es necesario escribir el nombre a mano.</p>
    </HelpHint>
  </header>

  {#if error}<p class="alert error">{error}</p>{/if}
  {#if info}<p class="alert ok">{info}</p>{/if}

  <div class="panel">
    <div class="row">
      <label class="field">
        <span>Consultorio / clínica</span>
        <select bind:value={consultorioId} disabled={cargando || !destinos.length}>
          {#each destinos as d}
            <option value={d.consultorioId}>
              {d.nombre}{d.vinculo === 'EXTERNO' ? ` · ${d.etiqueta}` : ''}
            </option>
          {/each}
        </select>
      </label>
      <label class="field">
        <span>Día</span>
        <input type="date" bind:value={dia} />
      </label>
    </div>

    <h3 class="sub">Ocupación del día</h3>
    {#if cargando}
      <p class="muted">Cargando…</p>
    {:else if !slots.length}
      <p class="muted">Sin citas registradas para este día (espacios libres).</p>
    {:else}
      <ul class="slot-list">
        {#each slots as s}
          <li>
            <strong>{new Date(s.inicio).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })}</strong>
            {#if s.consultorio} · {s.consultorio}{/if}
            {#if s.ocupado}<span class="badge-ocupado">ocupado</span>{/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  <form class="panel" onsubmit={agendar}>
    <h3 class="sub">Nueva cita</h3>
    <div class="grid-2">
      <label class="field"><span>Paciente</span><input required bind:value={pacienteNombre} placeholder="Nombre completo" /></label>
      <label class="field"><span>Teléfono</span><input bind:value={telefono} placeholder="+502" /></label>
      <label class="field"><span>Profesional</span>
        <select bind:value={profesional} required>
          <option value="">— Seleccionar profesional —</option>
          {#each profesionales as p (p.id)}
            <option value={p.nombre}>{p.nombre}{p.colegiado ? ` · Col. ${p.colegiado}` : ''}</option>
          {/each}
        </select>
        {#if profesionales.length === 0}
          <small class="muted">Registre profesionales en Administración → Profesionales.</small>
        {/if}
      </label>
      <label class="field"><span>Hora</span><input type="time" bind:value={hora} required /></label>
      <label class="field"><span>Duración (min)</span><input type="number" min="15" step="15" bind:value={duracionMin} /></label>
      <label class="field span-2"><span>Motivo</span><input bind:value={motivo} placeholder="Consulta, control…" /></label>
    </div>
    <button type="submit" class="btn primary" disabled={!consultorioId}>✓ Agendar cita</button>
  </form>
</section>

<style>
  .agenda-central { max-width: 960px; }
  .row { display: flex; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem; }
  .field { display: flex; flex-direction: column; gap: 0.35rem; min-width: 200px; }
  .field span { font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em; }
  .field input, .field select {
    padding: 0.6rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font: inherit;
    font-size: 0.9rem;
    color: #111827;
    background: #fff;
  }
  .field input:focus, .field select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12); }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin-bottom: 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .sub { margin: 0 0 1rem; font-size: 1rem; font-weight: 700; color: #166534; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .span-2 { grid-column: span 2; }
  .slot-list { margin: 0; padding-left: 1.2rem; }
  .slot-list li { padding: 0.3rem 0; }
  .badge-ocupado { margin-left: 0.4rem; font-size: 0.72rem; background: #fee2e2; color: #dc2626; padding: 0.1rem 0.5rem; border-radius: 999px; }
  .btn.primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding: 0.8rem 2rem;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(135deg, #15803d, #16a34a);
    color: #fff;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(22, 163, 74, 0.35);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .btn.primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(22, 163, 74, 0.45);
  }
  .btn.primary:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
  .alert.error { color: #dc2626; }
  .alert.ok { color: #16a34a; }
  .muted { color: #6b7280; }
  @media (max-width: 640px) { .grid-2 { grid-template-columns: 1fr; } .span-2 { grid-column: span 1; } }
</style>
